import { compact } from 'lodash';
import {
  IconBookOpen5OutlineDuo18,
  IconChart3OutlineDuo18,
  IconClipboardContentOutlineDuo18,
  IconCursorRippleOutlineDuo18,
  IconGauge7OutlineDuo18,
  IconGridOutlineDuo18,
  IconHeartHandshakeOutlineDuo18,
  IconHouseOutlineDuo18,
  IconNut2OutlineDuo18,
  IconRocketOutlineDuo18,
  IconSquareLayoutGrid4OutlineDuo18,
  IconTheatreMask2OutlineDuo18,
  IconUpload3OutlineDuo18,
  IconUsersHeartOutlineDuo18,
} from 'nucleo-ui-outline-duo-18';

import { TFunction } from '@lyearn/i18n';
import {
  BookmarkOutline,
  CompassOutline,
  File,
  MessageCircleOutline,
  NucleoAllTask,
  NucleoBarChartOutline,
  NucleoBookOpen,
  NucleoCalendar,
  NucleoCameraRecordFilmMeetings,
  NucleoContent,
  NucleoCrosshairsTarget,
  NucleoDialogue1On1ChatTalk,
  NucleoFeedbackDialogueThinkSpeech,
  NucleoFolderOpen,
  NucleoHabitsLeaf,
  NucleoListTree,
  NucleoPraiseStar,
  NucleoStack,
  NucleoSurvey,
  NucleoUnorderedList,
  NucleoUser,
  TvOutline,
} from '@lyearn/icons';
import { FeatureFlags, FeatureFlagsMap } from '@/helper/FeatureFlags';
import { FunctionalPermissionKey } from '@/helper/FunctionalPermissions';
import { DEFAULT_FEEDBACK_TAB } from '@/modules/corporate/modules/feedback/views/FeedbackWall/consts';
import { PraisesTabEnum } from '@/modules/praises/views/PraiseWall/types';
import { QuestionManagerTabEnum } from '@/modules/question/views/QuestionManager/types';
import { MyUserSurveysTabType } from '@/modules/user-survey/views/MyUserSurveys/types';
import routes from '@/pages/corporate/routes';
import workeffyAdminRoutes from '@/pages/corporate/routes/workeffy';

import { NAV_GROUP_IDS, NAV_IDS, NAV_SECTION_IDS } from '../consts';
import { NavItem, NavSection, NavSubGroup, NavSubItem, UnifiedNav } from '../types';

export interface BuildUnifiedNavOptions {
  t: TFunction;
  flags: FeatureFlagsMap;
  communityId?: string;
  defaultWidgetId?: string;
  isQuestionBankDisabled?: boolean;
  hasPermission: (permission: FunctionalPermissionKey) => boolean;
}

const toGroup = (id: string, items: (NavSubItem | null)[]): NavSubGroup | null => {
  const enabledItems = compact(items);
  const hasRealContent = enabledItems.some((item) => !item.isDisabled);

  return hasRealContent ? { id, items: enabledItems } : null;
};

const toParent = (item: NavItem, groups: (NavSubGroup | null)[]): NavItem | null => {
  const enabledGroups = compact(groups);

  return enabledGroups.length ? { ...item, groups: enabledGroups } : null;
};

const toSection = (id: string, items: (NavItem | null)[]): NavSection | null => {
  const enabledItems = compact(items);

  return enabledItems.length ? { id, items: enabledItems } : null;
};

// @DEV_CHECKPOINT - sidebar
export const buildUnifiedNav = ({
  t,
  flags,
  communityId,
  defaultWidgetId,
  isQuestionBankDisabled,
  hasPermission,
}: BuildUnifiedNavOptions): UnifiedNav => {
  const {
    [FeatureFlags.EnableOnboarding]: isOnboardingEnabled,
    [FeatureFlags.EnabledMGSAOnboarding]: isMgsaOnboardingEnabled,
    [FeatureFlags.EnableProductivityModel]: isProductivityModelEnabled,
    [FeatureFlags.EnablePerformModule]: isPerformModuleEnabled,
    [FeatureFlags.EnableLearnModule]: isLearnModuleEnabled,
    [FeatureFlags.EnableEngageModule]: isEngageModuleEnabled,
    [FeatureFlags.EnableOKR]: isOkrEnabled,
    [FeatureFlags.EnableHabits]: isHabitsEnabled,
    [FeatureFlags.EnableEmployeeJourney]: isEmployeeJourneyEnabled,
    [FeatureFlags.EnableUserSurvey]: isUserSurveyEnabled,
    [FeatureFlags.EnableMeeting]: isMeetingEnabled,
    [FeatureFlags.EnableOneOnOne]: isOneOnOneEnabled,
    [FeatureFlags.EnablePraise]: isPraiseEnabled,
    [FeatureFlags.EnableFeedback]: isFeedbackEnabled,
  } = flags;

  const primary = toSection(NAV_SECTION_IDS.Primary, [
    (isOnboardingEnabled || isMgsaOnboardingEnabled) &&
    hasPermission(FunctionalPermissionKey.ManageAcademy)
      ? {
          id: NAV_IDS.GetStarted,
          label: t('Admin:GettingStarted'),
          icon: IconRocketOutlineDuo18,
          href: routes.GettingStarted(),
        }
      : null,
    {
      id: NAV_IDS.Home,
      label: t('Common:Routes.Home'),
      icon: IconHouseOutlineDuo18,
      href: routes.Home(),
    },
    hasPermission(FunctionalPermissionKey.UseAgentDraft) ||
    hasPermission(FunctionalPermissionKey.ManageOrgChart)
      ? {
          id: NAV_IDS.Actions,
          label: t('Common:Routes.Actions', 'Actions'),
          icon: IconCursorRippleOutlineDuo18,
          href: routes.WorkeffyActions(),
          matchPrefix: true,
        }
      : null,
    hasPermission(FunctionalPermissionKey.UseCustomAgent)
      ? {
          id: NAV_IDS.Agents,
          label: t('Common:Routes.Agents', 'Agents'),
          icon: IconTheatreMask2OutlineDuo18,
          href: routes.WorkeffyAgents(),
          matchPrefix: true,
        }
      : null,
  ]);

  const people = toParent(
    {
      id: NAV_IDS.People,
      label: t('Common:Routes.People', 'People'),
      icon: IconUsersHeartOutlineDuo18,
    },
    [
      toGroup(NAV_GROUP_IDS.PeopleMain, [
        {
          id: NAV_IDS.PeoplePulse,
          label: t('Common:Routes.PeoplePulse', 'People Pulse'),
          icon: NucleoBarChartOutline,
          isDisabled: true,
        },
        hasPermission(FunctionalPermissionKey.ViewOrgChart)
          ? {
              id: NAV_IDS.OrgStructure,
              label: t('Common:Routes.OrgStructure', 'Org Structure'),
              icon: NucleoListTree,
              href: routes.WorkeffyOrgStructure(),
              matchPrefix: true,
            }
          : null,
        hasPermission(FunctionalPermissionKey.ManageUsers)
          ? {
              id: NAV_IDS.Employees,
              label: t('Admin:Employees'),
              icon: NucleoUser,
              href: workeffyAdminRoutes.WorkeffyEmployees(),
              matchPrefix: true,
            }
          : null,
      ]),
    ],
  );

  const productivity = toParent(
    {
      id: NAV_IDS.Productivity,
      label: t('Common:Routes.Productivity', 'Productivity'),
      icon: IconGauge7OutlineDuo18,
    },
    [
      toGroup(NAV_GROUP_IDS.ProductivityOverview, [
        {
          id: NAV_IDS.ProductivityPulse,
          label: t('Common:Routes.ProductivityPulse', 'Productivity Pulse'),
          icon: NucleoBarChartOutline,
          isDisabled: true,
        },
        isProductivityModelEnabled && hasPermission(FunctionalPermissionKey.ManageProductivityModel)
          ? {
              id: NAV_IDS.Mgsa,
              label: t('Common:Routes.MGSA', 'Configure MGSA'),
              icon: NucleoStack,
              href: workeffyAdminRoutes.WorkeffyMgsa(),
              matchPrefix: true,
            }
          : null,
      ]),
      toGroup(NAV_GROUP_IDS.ProductivityOkrs, [
        hasPermission(FunctionalPermissionKey.UseOkr)
          ? {
              id: NAV_IDS.Okrs,
              label: t('Common:Routes.OKRs'),
              icon: NucleoCrosshairsTarget,
              href: routes.WorkEffyAllOKRs(),
            }
          : null,
        hasPermission(FunctionalPermissionKey.UseOkr)
          ? {
              id: NAV_IDS.Cycles,
              label: t('Common:Routes.Cycles'),
              icon: NucleoCalendar,
              href: routes.WorkEffyManageCycles(),
            }
          : null,
      ]),
      toGroup(NAV_GROUP_IDS.ProductivityPerform, [
        hasPermission(FunctionalPermissionKey.ViewTask)
          ? {
              id: NAV_IDS.Tasks,
              label: t('Common:Routes.Tasks'),
              icon: NucleoAllTask,
              href: routes.Tasks(),
              matchPrefix: true,
            }
          : null,
        hasPermission(FunctionalPermissionKey.ViewHabit)
          ? {
              id: NAV_IDS.Habits,
              label: t('Common:Routes.Habits'),
              icon: NucleoHabitsLeaf,
              href: routes.Habits(),
              matchPrefix: true,
            }
          : null,
      ]),
    ],
  );

  const learning = isLearnModuleEnabled
    ? toParent(
        {
          id: NAV_IDS.Learning,
          label: t('Common:Routes.Learning', 'Learning'),
          icon: IconBookOpen5OutlineDuo18,
        },
        [
          toGroup(NAV_GROUP_IDS.LearningOverview, [
            {
              id: NAV_IDS.LearningPulse,
              label: t('Common:Routes.LearningPulse', 'Learning Pulse'),
              icon: NucleoBarChartOutline,
              isDisabled: true,
            },
            hasPermission(FunctionalPermissionKey.ViewContent)
              ? {
                  id: NAV_IDS.Library,
                  label: t('Common:Routes.Library'),
                  icon: NucleoBookOpen,
                  href: defaultWidgetId ? routes.Discover(defaultWidgetId) : undefined,
                  isDisabled: !defaultWidgetId,
                }
              : null,
            hasPermission(FunctionalPermissionKey.ViewBookmarks)
              ? {
                  id: NAV_IDS.Bookmark,
                  label: t('Common:Routes.Bookmark', 'Bookmark'),
                  icon: BookmarkOutline,
                  href: routes.Bookmarks(),
                }
              : null,
          ]),
          toGroup(NAV_GROUP_IDS.LearningContent, [
            hasPermission(FunctionalPermissionKey.ManageContent)
              ? {
                  id: NAV_IDS.AllContent,
                  label: t('Admin:AllContent'),
                  icon: NucleoContent,
                  href: routes.LearningContent(),
                }
              : null,
            hasPermission(FunctionalPermissionKey.ManageContent)
              ? {
                  id: NAV_IDS.Collections,
                  label: t('Admin:Collections'),
                  icon: NucleoFolderOpen,
                  href: routes.CollectionManager(),
                  matchPrefix: true,
                }
              : null,
            isQuestionBankDisabled || !hasPermission(FunctionalPermissionKey.ManageContent)
              ? null
              : {
                  id: NAV_IDS.QuestionBank,
                  label: t('Admin:QuestionBank'),
                  icon: NucleoUnorderedList,
                  href: routes.AdminQuestionManager(QuestionManagerTabEnum.Active),
                },
          ]),
          toGroup(NAV_GROUP_IDS.LearningDelivery, [
            // Opens the learner sessions page; the admin manager is reached from
            // there via Manage Sessions, which is gated on MANAGE_SESSION.
            hasPermission(FunctionalPermissionKey.ViewSession)
              ? {
                  id: NAV_IDS.Sessions,
                  label: t('Common:Routes.Sessions'),
                  icon: TvOutline,
                  href: routes.DiscoverAllEvent(),
                }
              : null,
            hasPermission(FunctionalPermissionKey.ManageAssignments)
              ? {
                  id: NAV_IDS.Assignment,
                  label: t('Common:Routes.Assignment', 'Assignment'),
                  icon: File,
                  href: routes.Assignments(),
                  matchPrefix: true,
                }
              : null,
            isEmployeeJourneyEnabled && hasPermission(FunctionalPermissionKey.ViewJourney)
              ? {
                  id: NAV_IDS.Journeys,
                  label: t('Common:Routes.Journeys'),
                  icon: CompassOutline,
                  href: routes.JourneyListing(),
                  matchPrefix: true,
                }
              : null,
          ]),
        ],
      )
    : null;

  const engagement = isEngageModuleEnabled
    ? toParent(
        {
          id: NAV_IDS.Engagement,
          label: t('Common:Routes.Engagement', 'Engagement'),
          icon: IconHeartHandshakeOutlineDuo18,
        },
        [
          toGroup(NAV_GROUP_IDS.EngagementMain, [
            isUserSurveyEnabled && hasPermission(FunctionalPermissionKey.ViewSurvey)
              ? {
                  id: NAV_IDS.Surveys,
                  label: t('Common:Routes.Surveys'),
                  icon: NucleoSurvey,
                  href: routes.MySurveys(MyUserSurveysTabType.Surveys),
                }
              : null,
            communityId && hasPermission(FunctionalPermissionKey.ViewCommunity)
              ? {
                  id: NAV_IDS.Community,
                  label: t('Common:Routes.Community'),
                  icon: MessageCircleOutline,
                  href: routes.CommunityDetails(communityId),
                  matchPrefix: true,
                }
              : null,
            isMeetingEnabled && hasPermission(FunctionalPermissionKey.ViewMeeting)
              ? {
                  id: NAV_IDS.Meetings,
                  label: t('Common:Routes.Meetings'),
                  icon: NucleoCameraRecordFilmMeetings,
                  href: routes.teamMeetingsDashboard(),
                  matchPrefix: true,
                }
              : null,
            isOneOnOneEnabled && hasPermission(FunctionalPermissionKey.ViewOneOnOne)
              ? {
                  id: NAV_IDS.OneOnOne,
                  label: t('Common:Routes.OneOnOne', '1:1'),
                  icon: NucleoDialogue1On1ChatTalk,
                  href: routes.oneOnOnes(),
                  matchPrefix: true,
                }
              : null,
            isPraiseEnabled && hasPermission(FunctionalPermissionKey.ViewPraise)
              ? {
                  id: NAV_IDS.Praise,
                  label: t('Common:Routes.Praise'),
                  icon: NucleoPraiseStar,
                  href: routes.Praises(PraisesTabEnum.All),
                  matchPrefix: true,
                }
              : null,
            isFeedbackEnabled && hasPermission(FunctionalPermissionKey.ViewFeedback)
              ? {
                  id: NAV_IDS.Feedback,
                  label: t('Common:Routes.Feedback'),
                  icon: NucleoFeedbackDialogueThinkSpeech,
                  href: routes.Feedback(DEFAULT_FEEDBACK_TAB),
                  matchPrefix: true,
                }
              : null,
          ]),
        ],
      )
    : null;

  const modules = toSection(NAV_SECTION_IDS.Modules, [
    people,
    productivity,
    learning,
    engagement,
    hasPermission(FunctionalPermissionKey.ViewReport)
      ? {
          id: NAV_IDS.Analytics,
          label: t('Common:Routes.Analytics'),
          icon: IconChart3OutlineDuo18,
          href: routes.LearnerReports(),
          matchPrefix: true,
        }
      : null,
    // The analytics SPA (effy-fe `apps/analytics`) under /a — a separate app, so
    // every row here is a full page load. Mirrors its own sidebar's grouping.
    hasPermission(FunctionalPermissionKey.ViewReport)
      ? toParent(
          {
            id: NAV_IDS.AnalyticsV2,
            label: t('Common:Routes.Analytics'),
            badge: t('Common:New'),
            icon: IconChart3OutlineDuo18,
          },
          [
            toGroup(NAV_GROUP_IDS.AnalyticsV2Main, [
              {
                id: NAV_IDS.AnalyticsV2Overview,
                label: t('Common:Routes.Overview'),
                icon: IconGridOutlineDuo18,
                href: routes.AnalyticsAppOverview(),
              },
              {
                id: NAV_IDS.AnalyticsV2Dashboards,
                label: t('Common:Routes.Dashboards'),
                icon: IconSquareLayoutGrid4OutlineDuo18,
                href: routes.AnalyticsAppDashboards(),
                matchPrefix: true,
              },
              {
                id: NAV_IDS.AnalyticsV2Reports,
                label: t('Common:Reports'),
                icon: IconClipboardContentOutlineDuo18,
                href: routes.AnalyticsAppReports(),
                matchPrefix: true,
              },
              {
                id: NAV_IDS.AnalyticsV2Exports,
                label: t('Common:Routes.Exports'),
                icon: IconUpload3OutlineDuo18,
                href: routes.AnalyticsAppExports(),
                matchPrefix: true,
              },
            ]),
          ],
        )
      : null,
  ]);

  const footer = toSection(NAV_SECTION_IDS.Footer, [
    hasPermission(FunctionalPermissionKey.ManageAcademy)
      ? {
          id: NAV_IDS.Settings,
          label: t('Common:Actions.Settings', 'Settings'),
          icon: IconNut2OutlineDuo18,
          href: routes.Settings(),
          matchPrefix: true,
        }
      : null,
  ]);

  return compact([primary, modules, footer]);
};
